import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Check, CircleAlert, Circle, KeyRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiErrorMessage } from '../services/api';
import { Brand, Button, Field, InfinityMark } from '../components/ui';
import './Auth.css';

// Mesmas regras do backend (Passwords.validate)
const RULES = [
  { label: 'Pelo menos 8 caracteres', test: (p) => p.length >= 8 },
  { label: 'Letras e números', test: (p) => /[A-Za-zÀ-ÿ]/.test(p) && /\d/.test(p) },
];

/** Troca de senha obrigatória no primeiro acesso (ou após o admin redefinir a senha). */
function ChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!user) return <Navigate to="/login" replace />;

  const rulesOk = RULES.every(r => r.test(next));
  const matches = next.length > 0 && next === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!matches) {
      setError('A confirmação não confere com a nova senha.');
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(current, next);
      navigate('/', { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth__strip" aria-hidden="true" />
      <InfinityMark className="auth__watermark" strokeWidth={4} />

      <div className="auth__box">
        <div className="auth__brand">
          <Brand size="md" onDark />
          <span className="auth__slogan">Olá, {user.name.split(' ')[0]}!</span>
        </div>

        <form className="auth__card" onSubmit={handleSubmit}>
          <h1>Crie sua senha</h1>
          <p className="auth__lead">
            {user.mustChangePassword
              ? 'Por segurança, troque a senha temporária antes de continuar.'
              : 'Escolha uma nova senha para sua conta.'}
          </p>

          {error && (
            <div className="auth__error" role="alert">
              <CircleAlert size={18} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <Field label={user.mustChangePassword ? 'Senha temporária' : 'Senha atual'}>
            <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required autoFocus />
          </Field>
          <Field label="Nova senha">
            <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required />
          </Field>
          <ul className="auth__rules">
            {RULES.map(r => {
              const ok = r.test(next);
              return (
                <li key={r.label} className={ok ? 'is-ok' : ''}>
                  {ok ? <Check size={14} aria-hidden="true" /> : <Circle size={14} aria-hidden="true" />}
                  {r.label}
                </li>
              );
            })}
          </ul>
          <Field label="Confirme a nova senha">
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
          </Field>

          <Button type="submit" variant="accent" size="lg" block icon={KeyRound} disabled={submitting || !current || !rulesOk || !matches}>
            {submitting ? 'Salvando...' : 'Salvar nova senha'}
          </Button>
        </form>

        <p className="auth__foot">
          Não é você? <button type="button" onClick={logout}>Sair</button>
        </p>
      </div>
    </div>
  );
}

export default ChangePassword;
